import type { CurrentUser } from "@/context/AuthContext";
import { api } from "./api";

export interface Invoice {
  id: string;
  received_invoice_id: number;
  year: number;
  number: string;
  supplier_reference: string | null;
  supplier_name: string;
  date_issued: string;
  date_due: string | null;
  amount: string;
  amount_net: string | null;
  vat_amount: string | null;
  osnovica_izvor: string | null;
  currency: string;
  status: string;
  stanje: string;
  check_reason: string | null;
  synced_at: string;
  version: number;
  liquidation_version: number;
  block_reason: string | null;
  orders: { id: string; broj: string | null }[];
  sites?: { id: string; naziv: string }[];
}
export interface InvoiceSplit {
  konto: string;
  gradiliste_id: string;
  mjesto_troska: string | null;
  iznos: string;
}
export interface InvoiceControl {
  orders: { id: string; broj: string | null; gradiliste_id: string; gradiliste: string }[];
  receipt_confirmed: boolean;
  differences: { id: string; narudzba_id: string; vrsta: string; stanje: string }[];
  receipts: {
    id: string;
    narudzba_id: string;
    narudzba: string | null;
    broj_otpremnice: string | null;
    datum: string;
    gradiliste: string;
    name: string;
    unit: string;
    quantity: string;
    amount_net: string | null;
  }[];
  sites: { id: string; naziv: string; mjesto_troska: string | null }[];
  accounts: string[];
}
export interface InvoiceComparison {
  mode: "items" | "order_total";
  amount_net: string | null;
  expected_net: string | null;
  amount_difference: string | null;
  received_net: string | null;
  receipt_amount_difference: string | null;
  reasons: string[];
  has_difference: boolean;
  lines: {
    name: string;
    unit: string;
    invoiced: string;
    ordered: string;
    received: string;
    quantity_difference: string;
    receipt_difference: string;
    invoiced_net: string;
    expected_net: string | null;
    amount_difference: string | null;
    received_net: string | null;
    receipt_amount_difference: string | null;
  }[];
}
export interface InvoiceWrite {
  id: string;
  liquidation_version: number;
  stanje: string;
  pokusaji: number;
  zadnja_greska: string | null;
  notes: string | null;
  analytic_id: number | null;
  created_at: string;
}
export const invoiceStates: Record<string, string> = {
  za_provjeru: "Za provjeru",
  nepovezano: "Nepovezano",
  blokirano: "Blokirano",
  ceka_upravu: "Čeka upravu",
  ceka_placanje: "Čeka odobrenje plaćanja",
  odobreno_placanje: "Potvrdi u Minimaxu",
  potvrdeno_u_minimaxu: "Potvrđeno u Minimaxu",
  odbijeno_u_minimaxu: "Odbijeno u Minimaxu",
};
export const invoiceTiles = [
  { key: "za_provjeru", label: "Za provjeru", states: ["za_provjeru"] },
  { key: "blokirano", label: "Blokirano", states: ["blokirano"] },
  { key: "ceka_upravu", label: "Čeka upravu", states: ["ceka_upravu", "ceka_placanje"] },
  { key: "odobreno_placanje", label: "Potvrdi u Minimaxu", states: ["odobreno_placanje"] },
  { key: "nepovezano", label: "Nepovezano", states: ["nepovezano"] },
];
export function hasInvoicePermission(user: CurrentUser | null, action = "read") {
  return user?.permissions?.some((p) => p.module === "ulazni_racun" && p.action === action) ?? false;
}
export function costCenter(value: string | null) {
  return value?.trim() || "—";
}
/** Comparison products can carry more than two decimal places; display them exactly. */
export function invoiceMoney(value: string | null | undefined) {
  if (value == null || !/^-?\d+(?:\.\d+)?$/.test(value)) return "—";
  const [whole, fraction = ""] = value.split(".");
  return `${whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${fraction.replace(/0+$/, "").padEnd(2, "0")} €`;
}
/** NUMERIC(14,2) from R1: twelve whole digits, exact cents throughout. */
export function decimalUnits(value: string): bigint | null {
  const normalized = value.trim().replace(",", ".");
  if (!/^\d{1,12}(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [whole, fraction = ""] = normalized.split(".");
  return BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, "0"));
}
export function decimalString(value: string): string | null {
  const cents = decimalUnits(value);
  if (cents === null) return null;
  return `${cents / 100n}.${(cents % 100n).toString().padStart(2, "0")}`;
}
export function basisReason(net: string, vat: string, gross: string) {
  const n = decimalUnits(net),
    v = decimalUnits(vat),
    g = decimalUnits(gross);
  if (n === null || n <= 0n || v === null || g === null)
    return "Unesite pozitivnu osnovicu i nenegativan PDV s najviše dvije decimale.";
  return n + v === g ? "" : "Osnovica i PDV ne odgovaraju iznosu računa";
}
export function splitReason(split: InvoiceSplit[], net: string | null) {
  if (net === null) return "Nedostaje neto osnovica računa.";
  if (!split.length) return "Dodajte barem jedan redak raščlambe.";
  let sum = 0n;
  for (const line of split) {
    const amount = decimalUnits(line.iznos);
    if (amount === null) return "Unesite valjan iznos raščlambe s najviše dvije decimale.";
    sum += amount;
  }
  const total = `${sum / 100n}.${(sum % 100n).toString().padStart(2, "0")}`;
  if (sum !== decimalUnits(net))
    return `Zbroj raščlambe ${invoiceMoney(total)} nije jednak osnovici ${invoiceMoney(net)}`;
  if (split.some((line) => !line.konto.trim() || !line.gradiliste_id))
    return "Odaberite konto i gradilište svakog retka.";
  return "";
}
export const writeStates: Record<string, string> = {
  ceka: "Čeka upis",
  u_tijeku: "Upis u tijeku",
  uspjelo: "Upisano u Minimax",
  greska: "Greška upisa",
  nepodrzano: "Potreban ručni unos",
  konflikt: "Račun promijenjen u Minimaxu",
};
const root = "/nabava/ulazni-racuni";
export const receivedInvoices = {
  list: async (signal?: AbortSignal) => {
    const rows: Invoice[] = [];
    for (let offset = 0; ; offset += 200) {
      const page = (await api.get<Invoice[]>(root, { params: { offset, limit: 200 }, signal })).data;
      rows.push(...page);
      if (page.length < 200) return rows;
    }
  },
  detail: async (id: string, signal?: AbortSignal) => (await api.get<Invoice>(`${root}/${id}`, { signal })).data,
  control: async (id: string, signal?: AbortSignal) =>
    (await api.get<InvoiceControl>(`${root}/${id}/kontrola`, { signal })).data,
  comparison: async (id: string, signal?: AbortSignal) =>
    (await api.get<InvoiceComparison>(`${root}/${id}/usporedba`, { signal })).data,
  split: async (id: string, signal?: AbortSignal) =>
    (await api.get<InvoiceSplit[]>(`${root}/${id}/rasclamba`, { signal })).data,
  outbox: async (id: string, signal?: AbortSignal) =>
    (await api.get<InvoiceWrite[]>(`${root}/${id}/red-upisa`, { signal })).data,
  basis: async (invoice: Invoice, net: string, vat: string) =>
    (
      await api.patch<Invoice>(`${root}/${invoice.id}/osnovica`, {
        amount_net: decimalString(net),
        vat_amount: decimalString(vat),
        expected_version: invoice.version,
      })
    ).data,
  liquidate: async (invoice: Invoice, split: InvoiceSplit[]) =>
    (
      await api.post<Invoice>(`${root}/${invoice.id}/likvidiraj`, {
        expected_version: invoice.version,
        split: split.map((line) => ({
          ...line,
          konto: line.konto.trim(),
          mjesto_troska: line.mjesto_troska?.trim() || null,
          iznos: decimalString(line.iznos),
        })),
      })
    ).data,
  approve: async (id: string) => (await api.post<Invoice>(`${root}/${id}/odobri-placanje`)).data,
  returnToReview: async (invoice: Invoice, reason: string) =>
    (await api.post<Invoice>(`${root}/${invoice.id}/vrati-nikolini`, {
      reason: reason.trim(), expected_version: invoice.version,
    })).data,
  block: async (id: string, reason: string, razlika_ids: string[]) =>
    (await api.post<Invoice>(`${root}/${id}/blokiraj`, { reason, razlika_ids })).data,
};
